import { API_USAGE_MAX_RANGE_DAYS, type ApiUsageSeries } from '@headline/schemas'

/**
 * What the workspace API page's usage card shows, from the usage series (`ApiUsageSeries`, CLI-1041):
 * the window a range reads, the callouts and the chart's rows. Every date is a UTC day, as clipbook-api
 * counts them. One read serves both the range and "remaining this month": the window reaches back to
 * the month's first whenever the range starts later.
 */

export type UsageRange = '7' | '30' | '90' | 'all'
export type UsageGrouping = 'key' | 'none'

export const USAGE_RANGES: Array<{ value: UsageRange; label: string }> = [
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'Since the endpoint was created' },
]

/** The chart's one series when calls are not grouped by key. */
export const ALL_CALLS = 'calls'

const DAY_MS = 86_400_000
const isoDay = (at: Date) => at.toISOString().slice(0, 10)
const dayOf = (day: string) => new Date(`${day}T00:00:00Z`)
const addDays = (day: string, days: number) =>
  isoDay(new Date(dayOf(day).getTime() + days * DAY_MS))

export interface UsageWindow {
  /** The range's first day. */
  rangeFrom: string
  /** The first day of the current UTC month. */
  monthFrom: string
  /** What is read: [from, to), `to` being tomorrow so today counts. */
  from: string
  to: string
}

/** This UTC month so far: [its first day, tomorrow), so today counts. */
export function monthToDate(now: Date): { from: string; to: string } {
  const today = isoDay(now)
  return { from: `${today.slice(0, 7)}-01`, to: addDays(today, 1) }
}

export function usageWindow(range: UsageRange, now: Date, createdAt: string): UsageWindow {
  const today = isoDay(now)
  const to = addDays(today, 1)
  const earliest = addDays(to, -API_USAGE_MAX_RANGE_DAYS)
  const wanted = range === 'all' ? createdAt.slice(0, 10) : addDays(today, 1 - Number(range))
  const rangeFrom = wanted < earliest ? earliest : wanted
  const monthFrom = `${today.slice(0, 7)}-01`
  return { rangeFrom, monthFrom, from: rangeFrom < monthFrom ? rangeFrom : monthFrom, to }
}

interface UsageBar {
  dataKey: string
  label: string
}

export interface UsageView {
  /** Billable calls in the range, of the chosen key or all. */
  calls: number
  /** Billable calls this month across every key: what the monthly quota counts. */
  monthUsed: number
  /** One row per day of the range, zeros included, keyed by each bar's `dataKey`. */
  rows: Array<Record<string, string | number>>
  bars: UsageBar[]
}

function keyLabel(series: ApiUsageSeries, keyId: string): string {
  const key = series.keys.find((candidate) => candidate.id === keyId)
  return key ? key.name : 'Unknown key'
}

export function usageView(
  series: ApiUsageSeries,
  span: UsageWindow,
  { keyId, groupBy }: { keyId: string | null; groupBy: UsageGrouping },
): UsageView {
  const inRange = series.days.filter((row) => row.day >= span.rangeFrom && row.day < span.to)
  const chosen = keyId ? inRange.filter((row) => row.keyId === keyId) : inRange
  const monthUsed = series.days
    .filter((row) => row.day >= span.monthFrom && row.day < span.to)
    .reduce((sum, row) => sum + row.billable, 0)

  const keyIds = [...new Set(chosen.map((row) => row.keyId))]
  const bars: UsageBar[] =
    groupBy === 'key'
      ? keyIds.map((id) => ({ dataKey: id, label: keyLabel(series, id) }))
      : [{ dataKey: ALL_CALLS, label: 'Billable calls' }]

  const byDay = new Map<string, Record<string, string | number>>()
  for (let day = span.rangeFrom; day < span.to; day = addDays(day, 1)) {
    byDay.set(day, Object.fromEntries([['day', day], ...bars.map((bar) => [bar.dataKey, 0])]))
  }
  for (const row of chosen) {
    const entry = byDay.get(row.day)
    if (!entry) continue
    const dataKey = groupBy === 'key' ? row.keyId : ALL_CALLS
    entry[dataKey] = Number(entry[dataKey] ?? 0) + row.billable
  }

  return {
    calls: chosen.reduce((sum, row) => sum + row.billable, 0),
    monthUsed,
    rows: [...byDay.values()],
    bars,
  }
}

/** One internal key's calls in a series: never billed, so requests are what it used. */
export interface InternalKeyUsage {
  keyId: string
  name: string
  requests: number
}

/**
 * Clipbook's view of a series, split as the admin section shows it: the client's billable calls (what
 * its quota, chart and alerts count) beside each internal key's requests, every internal key listed,
 * a quiet one with zero. Reads the rows' own `internal` flag (clipbook-api's).
 */
export function internalUsage(series: ApiUsageSeries): {
  clientBillable: number
  internal: InternalKeyUsage[]
} {
  const requests = new Map<string, number>()
  let clientBillable = 0
  for (const row of series.days) {
    if (row.internal) requests.set(row.keyId, (requests.get(row.keyId) ?? 0) + row.requests)
    else clientBillable += row.billable
  }
  const internal: InternalKeyUsage[] = []
  for (const key of series.keys) {
    if (key.internal) {
      internal.push({ keyId: key.id, name: key.name, requests: requests.get(key.id) ?? 0 })
    }
  }
  internal.sort((a, b) => b.requests - a.requests || a.name.localeCompare(b.name))
  return { clientBillable, internal }
}

/** The usage read as a card needs it: still loading, not connected (503), failed, or a series. */
export type UsageRead =
  | { state: 'loading' }
  | { state: 'unconfigured' }
  | { state: 'error'; message: string }
  | { state: 'ready'; series: ApiUsageSeries }

/** A usage query as a card reads it: an error, still loading, not connected (null), or a series. */
export function readOf(query: {
  isPending: boolean
  error: Error | null
  data: ApiUsageSeries | null | undefined
}): UsageRead {
  if (query.error) return { state: 'error', message: query.error.message }
  if (query.isPending || query.data === undefined) return { state: 'loading' }
  if (query.data === null) return { state: 'unconfigured' }
  return { state: 'ready', series: query.data }
}
